/**
 * RequestsContext — the full client-request lifecycle.
 * Intake → match client → classify → task → assign → acknowledge → communicate
 * → request missing info → follow-up → status → files → escalate → close,
 * with a complete interaction history. Front-end simulation for now.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

export type Channel = 'phone' | 'email' | 'text' | 'website' | 'portal' | 'verbal';
export type ReqType = 'unclassified' | 'sample' | 'quote' | 'reorder' | 'artwork' | 'po' | 'status' | 'complaint' | 'invoice' | 'logistics';
export type ReqStatus = 'new' | 'classified' | 'in_progress' | 'waiting_client' | 'blocked' | 'escalated' | 'closed';
export type Dept = 'csr' | 'sales' | 'prepress' | 'logistics' | 'production' | 'quality' | 'accounting' | 'operations';

export const REQ_TYPES: { v: ReqType; label: string; dept: Dept }[] = [
  { v: 'sample', label: 'Sample', dept: 'csr' }, { v: 'quote', label: 'Quote', dept: 'sales' },
  { v: 'reorder', label: 'Reorder', dept: 'csr' }, { v: 'artwork', label: 'Artwork', dept: 'prepress' },
  { v: 'po', label: 'Purchase Order', dept: 'csr' }, { v: 'status', label: 'Status', dept: 'csr' },
  { v: 'complaint', label: 'Complaint', dept: 'quality' }, { v: 'invoice', label: 'Invoice', dept: 'accounting' },
  { v: 'logistics', label: 'Logistics', dept: 'logistics' },
];
export const CHANNELS: Channel[] = ['phone', 'email', 'text', 'website', 'portal', 'verbal'];
export const CONTACT_ROLES = ['Buyer', 'Artwork contact', 'AP contact', 'Shipping contact', 'Owner', 'Other'];

export interface ReqTask { id: string; title: string; owner: string; dept: Dept; status: 'assigned' | 'acknowledged' | 'in_progress' | 'done'; }
export interface Interaction { id: string; channel: Channel | 'system'; dir: 'in' | 'out' | 'sys'; text: string; time: string; }
export interface ReqFile { id: string; name: string; kind: string; }
export interface ReqContact { name: string; role: string; email?: string; }
export interface Request {
  id: string; created: string; source: Channel; clientId?: string; clientName: string;
  contact?: ReqContact; type: ReqType; summary: string; status: ReqStatus; priority: 'standard' | 'rush' | 'critical';
  tasks: ReqTask[]; interactions: Interaction[]; files: ReqFile[];
  missingInfo: string[]; followUp?: { date: string; note: string; done: boolean };
  escalation?: string; closeNote?: string;
}

let seq = 4;
const newId = () => `REQ-2026-${String(seq++).padStart(4, '0')}`;
const now = () => 'Just now';

const SEED: Request[] = [
  {
    id: 'REQ-2026-0001', created: 'Today 9:14 AM', source: 'phone', clientName: 'Acme Nutraceutical Packaging',
    contact: { name: 'Jane Buyer', role: 'Buyer', email: 'jane@acme.example' }, type: 'sample',
    summary: 'Needs samples for three nutraceutical pouch SKUs by Friday; SKU numbers unclear.', status: 'waiting_client', priority: 'rush',
    tasks: [{ id: 'TASK-2026-1101', title: 'Confirm SKUs and prepare sample request', owner: 'Fatima', dept: 'csr', status: 'acknowledged' }],
    interactions: [
      { id: 'i1', channel: 'phone', dir: 'in', text: 'Jane called requesting samples for 3 SKUs by Friday.', time: 'Today 9:14 AM' },
      { id: 'i2', channel: 'email', dir: 'out', text: 'Asked Jane to confirm the three SKU numbers from the last reorder.', time: 'Today 9:40 AM' },
    ],
    files: [], missingInfo: ['Exact SKU numbers'],
  },
  {
    id: 'REQ-2026-0002', created: 'Today 8:02 AM', source: 'email', clientName: 'Blue Harbor Foods',
    contact: { name: 'Marco Diaz', role: 'Artwork contact' }, type: 'artwork',
    summary: 'Sent updated artwork proof; wants delivery timing.', status: 'in_progress', priority: 'standard',
    tasks: [{ id: 'TASK-2026-1102', title: 'Review updated artwork proof', owner: 'Diego', dept: 'prepress', status: 'in_progress' }],
    interactions: [{ id: 'i3', channel: 'email', dir: 'in', text: 'Artwork update received with proof attached.', time: 'Today 8:02 AM' }],
    files: [{ id: 'f1', name: 'BlueHarbor_proof_v4.pdf', kind: 'artwork' }], missingInfo: [],
  },
  {
    id: 'REQ-2026-0003', created: 'Yesterday 3:20 PM', source: 'text', clientName: 'Peak Wellness',
    contact: { name: 'Sara Lin', role: 'AP contact' }, type: 'invoice',
    summary: 'Checking invoice status and tracking number for last order.', status: 'new', priority: 'standard',
    tasks: [], interactions: [{ id: 'i4', channel: 'text', dir: 'in', text: 'Text: where is my invoice + tracking?', time: 'Yesterday 3:20 PM' }],
    files: [], missingInfo: [],
  },
];

interface Ctx {
  requests: Request[];
  createRequest: (r: Partial<Request>) => string;
  classify: (id: string, type: ReqType) => void;
  setContact: (id: string, c: ReqContact) => void;
  createTask: (id: string, title: string, dept: Dept, owner: string) => void;
  acknowledgeTask: (id: string, taskId: string) => void;
  advanceTask: (id: string, taskId: string) => void;
  logInteraction: (id: string, channel: Channel, dir: 'in' | 'out', text: string) => void;
  requestInfo: (id: string, items: string[]) => void;
  scheduleFollowUp: (id: string, date: string, note: string) => void;
  updateStatus: (id: string, status: ReqStatus) => void;
  attachFile: (id: string, name: string, kind: string) => void;
  escalate: (id: string, note: string) => void;
  closeRequest: (id: string, note: string) => void;
}

const C = createContext<Ctx | null>(null);

export function RequestsProvider({ children }: { children: ReactNode }) {
  const [requests, setRequests] = useState<Request[]>(SEED);
  const patch = (id: string, fn: (r: Request) => Request) => setRequests((rs) => rs.map((r) => (r.id === id ? fn(r) : r)));
  const sys = (r: Request, text: string): Request => ({ ...r, interactions: [...r.interactions, { id: `s${Date.now()}${Math.random()}`, channel: 'system', dir: 'sys', text, time: now() }] });

  const createRequest = useCallback((r: Partial<Request>) => {
    const id = newId();
    const type = (r.type as ReqType) || 'unclassified';
    const req: Request = {
      id, created: now(), source: (r.source as Channel) || 'phone', clientId: r.clientId, clientName: r.clientName || 'Unknown client',
      contact: r.contact, type, summary: r.summary || '', status: type === 'unclassified' ? 'new' : 'classified', priority: (r.priority as Request['priority']) || 'standard',
      tasks: [], interactions: [{ id: `s${Date.now()}`, channel: 'system', dir: 'sys', text: `Intake created from ${(r.source as string) || 'phone'}.`, time: now() }],
      files: [], missingInfo: [],
    };
    setRequests((rs) => [req, ...rs]);
    return id;
  }, []);

  const classify = useCallback((id: string, type: ReqType) => patch(id, (r) => sys({ ...r, type, status: r.status === 'new' ? 'classified' : r.status }, `Classified as ${type}.`)), []);
  const setContact = useCallback((id: string, c: ReqContact) => patch(id, (r) => sys({ ...r, contact: c }, `Contact added: ${c.name} (${c.role}).`)), []);
  const createTask = useCallback((id: string, title: string, dept: Dept, owner: string) => patch(id, (r) => sys({ ...r, status: 'in_progress', tasks: [...r.tasks, { id: `TASK-2026-${1100 + r.tasks.length + Math.floor(Math.random() * 800)}`, title, owner, dept, status: 'assigned' }] }, `Task created & assigned to ${owner} (${dept}).`)), []);
  const acknowledgeTask = useCallback((id: string, taskId: string) => patch(id, (r) => sys({ ...r, tasks: r.tasks.map((t) => (t.id === taskId ? { ...t, status: 'acknowledged' } : t)) }, `Task ${taskId} acknowledged.`)), []);
  const advanceTask = useCallback((id: string, taskId: string) => patch(id, (r) => ({ ...r, tasks: r.tasks.map((t) => (t.id === taskId ? { ...t, status: t.status === 'assigned' ? 'acknowledged' : t.status === 'acknowledged' ? 'in_progress' : 'done' } : t)) })), []);
  const logInteraction = useCallback((id: string, channel: Channel, dir: 'in' | 'out', text: string) => patch(id, (r) => ({ ...r, interactions: [...r.interactions, { id: `i${Date.now()}`, channel, dir, text, time: now() }] })), []);
  const requestInfo = useCallback((id: string, items: string[]) => patch(id, (r) => sys({ ...r, status: 'waiting_client', missingInfo: [...new Set([...r.missingInfo, ...items])] }, `Requested missing info from client: ${items.join(', ')}.`)), []);
  const scheduleFollowUp = useCallback((id: string, date: string, note: string) => patch(id, (r) => sys({ ...r, followUp: { date, note, done: false } }, `Follow-up scheduled for ${date}.`)), []);
  const updateStatus = useCallback((id: string, status: ReqStatus) => patch(id, (r) => sys({ ...r, status }, `Status updated to ${status.replace('_', ' ')}.`)), []);
  const attachFile = useCallback((id: string, name: string, kind: string) => patch(id, (r) => sys({ ...r, files: [...r.files, { id: `f${Date.now()}`, name, kind }] }, `File attached: ${name}.`)), []);
  const escalate = useCallback((id: string, note: string) => patch(id, (r) => sys({ ...r, status: 'escalated', priority: 'critical', escalation: note }, `Escalated to Operations: ${note}`)), []);
  const closeRequest = useCallback((id: string, note: string) => patch(id, (r) => sys({ ...r, status: 'closed', closeNote: note }, `Request closed: ${note}`)), []);

  return <C.Provider value={{ requests, createRequest, classify, setContact, createTask, acknowledgeTask, advanceTask, logInteraction, requestInfo, scheduleFollowUp, updateStatus, attachFile, escalate, closeRequest }}>{children}</C.Provider>;
}

export function useRequests() {
  const c = useContext(C);
  if (!c) throw new Error('useRequests must be used within RequestsProvider');
  return c;
}
